import { Box, Button, Container, Heading, HStack, Text, VStack, useDisclosure, useBreakpointValue } from "@chakra-ui/react";
import { motion } from 'framer-motion';
import { usePushTransition } from '@/hooks/usePushTransition';
import { MdContentCopy as CopyIcon, MdPlaylistAdd as AddRecipesIcon, MdDeleteOutline as DeleteIcon, MdDriveFileRenameOutline as RenameIcon } from "react-icons/md";
import { useState, useEffect } from "react";
import { Tabs } from "@chakra-ui/react/tabs";
import { toaster } from "@/lib/toaster";
import { useLocation, useParams } from "wouter";
import { useCollectionById } from '@/lib/collectionService';
import { useRecipesByIds } from '@/lib/recipeService';
import { useRecipeCollectionActions } from "@/hooks/useRecipeCollectionActions";
import Header from "@/components/Header";
import BottomNavBar from "@/components/BottomNavBar";
import AddRecipesModal from "@/components/AddRecipesModal";
import RenameCollectionModal from "@/components/RenameCollectionModal";
import ShoppingListTab from "@/components/ShoppingListTab";
import RecipesTab from "@/components/RecipesTab";
import CollectionLoadingSkeleton from "@/components/CollectionLoadingSkeleton";
import CollectionHeroStrip from "@/components/CollectionHeroStrip";
import FloatingAIChefButton from "@/components/FloatingAIChefButton";

const MotionBox = motion(Box);

const SIDEBAR_WIDTH = "320px";
const SIDEBAR_COLLAPSED_WIDTH = "28px";

export default function RecipeGroupDetail() {
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });
  const [activeTab, setActiveTab] = useState('recipes');
  const [renameOpen, setRenameOpen] = useState(false);
  const [shoppingCollapsed, setShoppingCollapsed] = useState(false);
  const [, navigate] = useLocation();
  const params = useParams();
  const { open, onOpen, onClose } = useDisclosure();

  const groupId = params.id || null;

  const { collection: group, isLoading } = useCollectionById(groupId ?? undefined);
  const { recipes: groupRecipes, isLoading: recipesLoading } = useRecipesByIds(group?.recipeIds ?? []);
  const { motionProps, handleBack } = usePushTransition({ ready: !!group });

  const {
    addRecipes,
    rename,
    reorderIngredients,
    generateIngredients,
    isAddingRecipes,
    isDeletingGroup,
    isRenaming,
    isRemovingRecipe,
    isGeneratingIngredients,
    handleDeleteGroup,
    handleIngredientToggle,
    handleRemoveRecipe,
  } = useRecipeCollectionActions({
    groupId,
    onDeleteSuccess: () => navigate('/recipe-groups'),
  });

  useEffect(() => {
    if (group?.ingredientsNeedRegeneration && !isGeneratingIngredients) {
      void generateIngredients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.ingredientsNeedRegeneration]);

  const copyShoppingList = () => {
    if (!group?.ingredients) return;

    const ingredientsToShop = group.ingredients.filter(ingredient => ingredient.status !== 'have');

    if (ingredientsToShop.length === 0) {
      toaster.create({
        title: "All ingredients are already marked as have",
        description: "Nothing left to shop for!",
      });
      return;
    }

    const shoppingListText = ingredientsToShop.map((ingredient) =>
      `• ${ingredient.text}`
    ).join('\n');

    navigator.clipboard.writeText(shoppingListText).then(() => {
      toaster.create({
        title: "Shopping list copied to clipboard",
        description: `${ingredientsToShop.length} ingredient${ingredientsToShop.length === 1 ? '' : 's'} copied`,
      });
    }).catch(() => {
      toaster.create({
        title: "Failed to copy shopping list",
        description: "Please try again",
      });
    });
  };

  const handleAddRecipes = (recipeIds: string[]) => {
    addRecipes(recipeIds).then(() => onClose());
  };

  const itemsToShop = group?.ingredients?.filter(i => i.status !== 'have').length ?? 0;

  if (isMobile === undefined || isLoading) {
    return (
      <Box bg="bg.canvas" minH="100vh" display="flex" flexDirection="column" pb={{ base: "80px", lg: 0 }}>
        {isMobile ? (
          <Container maxW="container.lg" py={0}>
            <CollectionLoadingSkeleton />
          </Container>
        ) : (
          <CollectionLoadingSkeleton variant="desktop" />
        )}
        <BottomNavBar />
      </Box>
    );
  }

  if (!group) {
    return (
      <Box bg="bg.canvas" minH="100vh" pb={{ base: "80px", lg: 0 }}>
        {isMobile && (
          <Header
            title="Collection Not Found"
            showBackButton={true}
            onBackClick={() => window.history.back()}
          />
        )}
        <Container maxW="container.lg" py={8}>
          <Text>Collection not found</Text>
        </Container>
        <BottomNavBar />
      </Box>
    );
  }

  return (
    <MotionBox bg="bg.canvas" minH="100vh" display="flex" flexDirection="column" pb={{ base: "80px", lg: 0 }} {...motionProps}>

      {/* ── Desktop layout ───────────────────────────────── */}
      {!isMobile && (
        <>
          {/* Header panel — matches RecipeView style */}
          <Box borderBottomWidth="1px" borderColor="border.muted" bg="bg.canvas" px={8} pt={6} pb={0} flexShrink={0}>
            <Box display="flex" gap={4} alignItems="flex-start">
              {/* Left: overline + title + recipe count */}
              <Box flex={1} minW={0}>
                <Text fontSize="9px" textTransform="uppercase" letterSpacing="0.12em" color="text.muted" fontWeight="600" mb={2}>Collection</Text>
                <Heading fontFamily="heading" fontWeight="800" fontSize="3xl" color="text.primary" lineHeight="1.05" letterSpacing="-0.02em" mb={3}>
                  {group.title}
                </Heading>
                <Box
                  display="inline-flex" alignItems="center"
                  fontSize="10px" fontWeight="500" px={2} py="2px"
                  borderRadius="full" bg="bg.muted" color="text.muted"
                  border="1px solid" borderColor="border.muted"
                >
                  {group.recipeIds?.length ?? 0} {(group.recipeIds?.length ?? 0) === 1 ? "recipe" : "recipes"}
                </Box>
              </Box>

              {/* Right: ghost action buttons + divider + primary CTA */}
              <HStack gap={1} flexShrink={0}>
                <Button
                  variant="ghost" size="sm" color="text.muted"
                  _hover={{ bg: "bg.muted", color: "text.primary" }}
                  fontFamily="body" fontWeight="500" fontSize="12px"
                  h="30px" px={2} borderRadius="md"
                  onClick={() => setRenameOpen(true)}
                >
                  <RenameIcon size={14} />
                  Rename
                </Button>
                <Button
                  variant="ghost" size="sm" color="text.muted"
                  _hover={{ bg: "bg.muted", color: "status.error" }}
                  fontFamily="body" fontWeight="500" fontSize="12px"
                  h="30px" px={2} borderRadius="md"
                  onClick={handleDeleteGroup}
                  loading={isDeletingGroup}
                >
                  <DeleteIcon size={14} />
                  Delete
                </Button>
                <Box w="1px" h="18px" bg="border.muted" mx={1} flexShrink={0} />
                <Button
                  bg="accent.emphasis" color="white" _hover={{ bg: "accent.emphasisHover" }}
                  fontFamily="heading" fontWeight="800" fontSize="xs"
                  letterSpacing="0.06em" textTransform="uppercase"
                  borderRadius="lg" h="34px" px={4}
                  onClick={onOpen}
                >
                  <AddRecipesIcon size={14} />
                  Add recipes
                </Button>
              </HStack>
            </Box>
            {/* Bottom tint strip — matches RecipeView */}
            <Box borderTopWidth="1px" borderColor="border.muted" mt={4} py={3} mx={-8} px={8} bg="bg.surface" />
          </Box>

          {/* Split body */}
          <Box flex={1} display="flex" overflow="hidden" minH={0}>

            {/* Left: recipe grid */}
            <Box
              flex={1}
              overflowY="auto"
              px={6}
              pt={5}
              pb={8}
              borderRightWidth="1px"
              borderColor="border.muted"
            >
              <Text
                fontSize="8px"
                textTransform="uppercase"
                letterSpacing="0.12em"
                color="text.muted"
                fontWeight="600"
                mb={3}
              >
                Recipes
              </Text>
              <RecipesTab
                recipes={groupRecipes}
                onRecipeClick={(recipeId) => navigate(`/recipes/${recipeId}`)}
                onRemoveRecipe={handleRemoveRecipe}
                isRemoving={isRemovingRecipe}
                isLoading={recipesLoading}
                twoColumnMax={!shoppingCollapsed}
              />
            </Box>

            {/* Right: shopping list — collapsed tab or expanded panel */}
            {shoppingCollapsed ? (
              <Box
                w={SIDEBAR_COLLAPSED_WIDTH}
                flexShrink={0}
                bg="bg.surface"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
                cursor="pointer"
                onClick={() => setShoppingCollapsed(false)}
                title="Expand shopping list"
                _hover={{ bg: "bg.muted" }}
                transition="background 0.15s"
              >
                <Text
                  fontSize="8px"
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                  color="text.muted"
                  fontWeight="600"
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                >
                  List
                </Text>
                {itemsToShop > 0 && (
                  <Box
                    bg="accent.emphasis"
                    color="white"
                    fontSize="9px"
                    fontWeight="700"
                    px="5px"
                    py="2px"
                    borderRadius="full"
                    lineHeight="1.4"
                  >
                    {itemsToShop}
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                w={SIDEBAR_WIDTH}
                flexShrink={0}
                bg="bg.surface"
                overflowY="auto"
                px={4}
                pt={5}
                pb={8}
                position="relative"
              >
                {/* Collapse toggle on left edge */}
                <Box
                  position="absolute"
                  left="-22px"
                  top="50%"
                  transform="translateY(-50%)"
                  w="22px"
                  h="56px"
                  bg="bg.surface"
                  borderWidth="1px"
                  borderColor="border.muted"
                  borderRightWidth={0}
                  borderStartRadius="md"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                  cursor="pointer"
                  onClick={() => setShoppingCollapsed(true)}
                  title="Collapse shopping list"
                  _hover={{ bg: "bg.muted" }}
                  transition="background 0.15s"
                  zIndex={1}
                >
                  <Text
                    fontSize="8px"
                    letterSpacing="0.1em"
                    textTransform="uppercase"
                    color="text.muted"
                    fontWeight="600"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    List
                  </Text>
                  {itemsToShop > 0 && (
                    <Box
                      bg="accent.emphasis"
                      color="white"
                      fontSize="8px"
                      fontWeight="700"
                      px="4px"
                      py="1px"
                      borderRadius="full"
                      lineHeight="1.4"
                    >
                      {itemsToShop}
                    </Box>
                  )}
                </Box>

                {/* Header: label + copy button */}
                <HStack justify="space-between" mb={3}>
                  <Text
                    fontSize="8px"
                    textTransform="uppercase"
                    letterSpacing="0.12em"
                    color="text.muted"
                    fontWeight="600"
                  >
                    Shopping List
                  </Text>
                  <Button
                    variant="ghost"
                    size="sm"
                    color="text.muted"
                    _hover={{ bg: "bg.muted", color: "text.primary" }}
                    fontFamily="body"
                    fontWeight="500"
                    fontSize="11px"
                    h="26px"
                    px={2}
                    borderRadius="md"
                    onClick={copyShoppingList}
                    gap={1}
                  >
                    <CopyIcon size={12} />
                    Copy
                  </Button>
                </HStack>

                <ShoppingListTab
                  group={group}
                  onToggle={handleIngredientToggle}
                  onReorder={(ingredientUpdates) => void reorderIngredients(ingredientUpdates)}
                  onGenerate={() => void generateIngredients()}
                  isGenerating={isGeneratingIngredients}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      {/* ── Mobile layout — unchanged ────────────────────── */}
      {isMobile && (
        <>
          <CollectionHeroStrip
            id={group.id}
            title={group.title}
            recipeIds={group.recipeIds ?? []}
            onBack={() => void handleBack()}
            onAddRecipes={onOpen}
            onRename={() => setRenameOpen(true)}
            onDelete={handleDeleteGroup}
            deleteLoading={isDeletingGroup}
          />

          <Container maxW="container.lg" pt={4} pb={8}>
            <VStack gap={6} align="stretch">
              <Tabs.Root value={activeTab} onValueChange={(d) => setActiveTab(d.value)}>
                <Box position="relative">
                  <Tabs.List>
                    <Tabs.Trigger value="recipes">Recipes ({group.recipeIds?.length ?? 0})</Tabs.Trigger>
                    <Tabs.Trigger value="shopping">Shopping List</Tabs.Trigger>
                  </Tabs.List>
                  {activeTab === 'shopping' && (
                    <Box position="absolute" right={0} top="50%" transform="translateY(-50%)">
                      <Button
                        size="sm"
                        variant="ghost"
                        color="text.muted"
                        fontFamily="body"
                        fontWeight="500"
                        _hover={{ color: "accent.secondary" }}
                        onClick={copyShoppingList}
                        gap={1}
                      >
                        <CopyIcon size={14} />
                        Copy
                      </Button>
                    </Box>
                  )}
                </Box>

                <Tabs.Content value="recipes" px={0}>
                  <RecipesTab
                    recipes={groupRecipes}
                    onRecipeClick={(recipeId) => navigate(`/recipes/${recipeId}`)}
                    onRemoveRecipe={handleRemoveRecipe}
                    isRemoving={isRemovingRecipe}
                    isLoading={recipesLoading}
                  />
                </Tabs.Content>

                <Tabs.Content value="shopping" px={0}>
                  <ShoppingListTab
                    group={group}
                    onToggle={handleIngredientToggle}
                    onReorder={(ingredientUpdates) => void reorderIngredients(ingredientUpdates)}
                    onGenerate={() => void generateIngredients()}
                    isGenerating={isGeneratingIngredients}
                  />
                </Tabs.Content>
              </Tabs.Root>
            </VStack>
          </Container>
        </>
      )}

      <AddRecipesModal
        open={open}
        onClose={onClose}
        group={group}
        onAddRecipes={handleAddRecipes}
        isAddingRecipes={isAddingRecipes}
      />

      <RenameCollectionModal
        open={renameOpen}
        currentTitle={group.title}
        isLoading={isRenaming}
        onConfirm={(newTitle) => {
          rename(newTitle).then(() => setRenameOpen(false));
        }}
        onClose={() => setRenameOpen(false)}
      />

      <FloatingAIChefButton collectionId={groupId || undefined} />
      <BottomNavBar collectionId={groupId || undefined} />
    </MotionBox>
  );
}
